import React from 'react';
import BoardThumbnail from './BoardThumbnail';
import Icon from '../ui/Icon';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/api$/, '');

const ColorDot = ({ color }) => (
  <span
    title={`Playing as ${color}`}
    className={`inline-block w-3 h-3 rounded-full ring-1 ${color === 'white' ? 'bg-ink-100 ring-ink-300' : 'bg-ink-950 ring-ink-500'}`}
  />
);

/**
 * Course Card Component
 * Single Responsibility: Rendering one course of the catalog with its progress
 */
const CourseCard = ({ opening, progress, liked, onOpen, onToggleLike }) => {
  const lineCount = opening.lines?.length || 0;
  const learnedPercent = lineCount ? (progress.learned / lineCount) * 100 : 0;
  const perfectedPercent = lineCount ? (progress.perfected / lineCount) * 100 : 0;

  let actionLabel = 'Start learning';
  if (progress.due > 0) actionLabel = `Review ${progress.due} line${progress.due > 1 ? 's' : ''}`;
  else if (progress.learned > 0 && progress.learned < lineCount) actionLabel = 'Continue';
  else if (progress.learned > 0) actionLabel = 'Practice';

  return (
    <article
      className="card group p-3 sm:p-4 flex gap-4 cursor-pointer transition-all duration-200 hover:border-brand-500/50 hover:-translate-y-0.5"
      onClick={onOpen}
    >
      <div className="w-24 sm:w-32 flex-shrink-0 self-start">
        {opening.image ? (
          <img
            src={`${API_ORIGIN}/storage/${opening.image}`}
            alt={`${opening.name} opening position`}
            className="w-full aspect-square object-cover rounded-xl"
          />
        ) : (
          <BoardThumbnail id={opening.id} moves={opening.lines?.[0]?.moves} orientation={opening.playerColor} />
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start gap-2">
          <h3 className="flex-1 text-base sm:text-lg font-bold leading-snug text-ink-100 group-hover:text-white">{opening.name}</h3>
          <span className="pt-1.5"><ColorDot color={opening.playerColor} /></span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {opening.isCommunity && (
            <span className="badge bg-brand-500/15 text-brand-300"><Icon name="globe" size={12} /> Community</span>
          )}
          {opening.difficulty && <span className="badge bg-ink-750 text-ink-300">{opening.difficulty}</span>}
          {opening.authorName && <span className="text-xs text-ink-400">by <span className="font-semibold text-ink-200">{opening.authorName}</span></span>}
        </div>

        <p className="text-ink-300 text-sm mt-2 line-clamp-2">{opening.description}</p>

        <div className="mt-auto pt-3">
          <div className="flex justify-between text-[11px] font-medium text-ink-400 mb-1.5">
            <span>{progress.learned > 0 ? `${progress.learned} learned · ${progress.perfected} perfected` : 'Not started'}</span>
            <span>{lineCount} line{lineCount > 1 ? 's' : ''}</span>
          </div>
          <div className="h-1.5 bg-ink-750 rounded-full overflow-hidden relative" role="progressbar" aria-valuenow={Math.round(learnedPercent)} aria-valuemin={0} aria-valuemax={100}>
            <div className="absolute inset-y-0 left-0 bg-brand-700 rounded-full" style={{ width: `${learnedPercent}%` }} />
            <div className="absolute inset-y-0 left-0 bg-brand-400 rounded-full" style={{ width: `${perfectedPercent}%` }} />
          </div>
          <div className="flex justify-between items-center mt-3">
            <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${progress.due > 0 ? 'text-amber-300' : 'text-brand-300'}`}>
              {actionLabel}
              <Icon name="arrowRight" size={15} className="transition-transform group-hover:translate-x-0.5" />
            </span>
            <button
              onClick={(event) => {
                event.stopPropagation();
                onToggleLike();
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold transition-colors ${
                liked ? 'text-rose-300 bg-rose-500/10' : 'text-ink-400 hover:text-ink-100 hover:bg-ink-750'
              }`}
              title={liked ? 'Unlike' : 'Like'}
              aria-pressed={liked}
            >
              <Icon name="heart" size={14} filled={liked} /> {opening.likesCount}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default CourseCard;
